import type {
  AdminOrgDto,
  BoardAccessDto,
  BoardAccessSettingsDto,
  BoardDto,
  BoardTemplateContent,
  BoardTemplateDto,
  BoardVisibility,
  FontDto,
  FrameNotesDto,
  MediaDto,
  MemberInviteDto,
  OrgDesign,
  MeDto,
  OrgDto,
  OrgMemberDto,
  OrgPlan,
  OrgRole,
  RedeemInfoDto,
  ShareLinkDto,
  ShareMode,
  SnapshotDto,
  TemplateApplyDto,
  TemplateContent,
  TemplateDto,
  UploadResultDto,
  TemplateScope,
  Translator,
  WaitingRoomDto,
} from '@praesto/shared'
import { isLanguage, translator, type Language } from '@praesto/shared'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Die Sprache, in der der Server antworten soll.
 *
 * Als Modulvariable und nicht als Parameter an jedem Aufruf: Die Sprache ändert
 * sich selten, betrifft aber jede der rund sechzig Funktionen unten. Gesetzt
 * wird sie vom `LanguageProvider`, sobald er die Sprache kennt — und damit vor
 * der ersten Anfrage.
 *
 * `Accept-Language` schickt der Browser ohnehin; wir überschreiben ihn, weil die
 * bewusste Wahl im Konto schwerer wiegt als die Einstellung des Browsers.
 */
let apiLanguage = 'de'

export function setApiLanguage(language: string): void {
  apiLanguage = language
}

/** Übersetzer für die beiden Meldungen, die hier statt vom Server entstehen. */
const text = (): Translator => translator(isLanguage(apiLanguage) ? apiLanguage : 'de')

/**
 * Versionsabgleich zwischen diesem Bundle und dem Server.
 *
 * Ein Tab, der seit einem Deploy offen steht, läuft mit altem JavaScript
 * weiter. Ändert sich dabei die Form einer Antwort, liest der alte Client sie
 * falsch — am 14.08.2026 entstanden so unsichtbare Elemente auf einem Board,
 * ohne Fehlermeldung, ohne Bild. Deshalb trägt jede API-Antwort die Kennung
 * des Clients, den der Server ausliefert; weicht sie von der eigenen ab, ist
 * dieses Bundle veraltet.
 *
 * Die eigene Kennung ist der Dateiname dieses Bundles — er steht in
 * `import.meta.url` und trägt den Inhaltshash. Im Entwicklungsbetrieb passt
 * das Muster nicht (Vite liefert Module einzeln aus); dann bleibt die Kennung
 * `null` und es wird nichts verglichen.
 */
const CLIENT_BUILD = /\/(index-[A-Za-z0-9_-]+\.js)/.exec(import.meta.url)?.[1] ?? null

let stale = false
const staleListeners = new Set<() => void>()

/** Ob der Server inzwischen einen neueren Client ausliefert als diesen hier. */
export const isStale = (): boolean => stale

export function onStale(listener: () => void): () => void {
  staleListeners.add(listener)
  return () => {
    staleListeners.delete(listener)
  }
}

/**
 * Einmal veraltet, immer veraltet: Neuladen ist der einzige Weg zurück, also
 * gibt es keinen Grund, den Hinweis je wieder zurückzunehmen.
 */
function checkBuild(response: Response): void {
  if (stale || !CLIENT_BUILD) return
  const serverBuild = response.headers.get('X-Praesto-Build')
  if (!serverBuild || serverBuild === CLIENT_BUILD) return
  stale = true
  for (const listener of staleListeners) listener()
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Content-Type nur setzen, wenn wirklich ein JSON-Body mitgeht: Fastify weist
  // eine leere Anfrage mit `application/json` mit 400 ab. Betrifft alle POSTs
  // ohne Nutzdaten (Logout, Gast-Link einlösen, Freigabe erzeugen).
  const hasJsonBody = typeof init?.body === 'string'
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
      'Accept-Language': apiLanguage,
      ...init?.headers,
    },
  })

  // Vor der Fehlerbehandlung: Auch eine abgelehnte Anfrage verrät, welcher
  // Client zu diesem Server gehört.
  checkBuild(response)

  if (!response.ok) {
    let code = 'error'
    let message = text()('error.requestFailed', { status: response.status })
    try {
      const body = (await response.json()) as { error?: string; message?: string }
      code = body.error ?? code
      message = body.message ?? message
    } catch {
      // Antwort war kein JSON — die Standardmeldung reicht.
    }
    throw new ApiError(response.status, code, message)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) })

export const api = {
  authStatus: () => request<{ needsBootstrap: boolean }>('/api/auth/status'),
  me: () => request<MeDto>('/api/auth/me'),
  login: (email: string, password: string) =>
    request<MeDto>('/api/auth/login', json({ email, password })),
  register: (email: string, password: string, displayName: string, orgName?: string) =>
    request<MeDto>('/api/auth/register', json({ email, password, displayName, orgName })),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/api/auth/password', json({ currentPassword, newPassword })),
  /** Merkt die Sprachwahl am Konto, damit sie auf jedes Gerät mitkommt. */
  setLanguage: (language: Language) =>
    request<void>('/api/auth/language', { method: 'PUT', body: JSON.stringify({ language }) }),
  /** Antwortet immer erfolgreich — auch für Adressen ohne Konto. */
  forgotPassword: (email: string) => request<void>('/api/auth/forgot', json({ email })),
  redeemInfo: (token: string) => request<RedeemInfoDto>(`/api/auth/redeem/${token}`),
  /** Legt eine Wegwerf-Organisation samt Board an und meldet den Besucher an. */
  startDemo: () =>
    request<{ boardId: string; me: MeDto; minutes: number }>('/api/demo', { method: 'POST' }),
  /**
   * Setzt den Besucher als Gast in die laufende Vorführung.
   *
   * Legt nichts an außer einer Gast-Sitzung — Board und Link stehen dauerhaft.
   */
  startShowcase: () =>
    request<{ boardId: string; mode: ShareMode }>('/api/showcase', { method: 'POST' }),
  signup: (body: { email: string; displayName: string; orgName: string; plan?: OrgPlan }) =>
    request<{ status: string }>('/api/signup', json(body)),
  redeem: (token: string, password: string, displayName?: string) =>
    request<MeDto>(`/api/auth/redeem/${token}`, json({ password, displayName })),

  orgs: () => request<OrgDto[]>('/api/orgs'),
  createOrg: (name: string, plan?: OrgPlan, licensedUntil?: string | null) =>
    request<OrgDto>('/api/orgs', json({ name, plan, licensedUntil })),

  /** Betreiberebene — für alle anderen antwortet die API mit 404. */
  adminOrgs: () => request<AdminOrgDto[]>('/api/admin/orgs'),
  setLicense: (
    id: string,
    body: {
      plan?: OrgPlan
      licensedUntil?: string | null
      memberLimit?: number | null
      storageLimitBytes?: number | null
    },
  ) => request<void>(`/api/admin/orgs/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteOrg: (id: string, confirmName: string) =>
    request<void>(`/api/admin/orgs/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirmName }),
    }),
  renameOrg: (id: string, name: string) =>
    request<void>(`/api/orgs/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
  setOrgDesign: (id: string, design: OrgDesign) =>
    request<void>(`/api/orgs/${id}/design`, { method: 'PUT', body: JSON.stringify(design) }),
  orgMembers: (id: string) => request<OrgMemberDto[]>(`/api/orgs/${id}/members`),
  addOrgMember: (id: string, body: { email: string; displayName?: string; role?: OrgRole }) =>
    request<MemberInviteDto>(`/api/orgs/${id}/members`, json(body)),
  setOrgRole: (id: string, userId: string, role: OrgRole) =>
    request<void>(`/api/orgs/${id}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
  removeOrgMember: (id: string, userId: string) =>
    request<{ boardsHandedOver: number }>(`/api/orgs/${id}/members/${userId}`, {
      method: 'DELETE',
    }),

  boards: (orgId: string) => request<BoardDto[]>(`/api/boards?org=${encodeURIComponent(orgId)}`),
  createBoard: (orgId: string, title?: string) =>
    request<BoardDto>('/api/boards', json({ orgId, title })),
  boardAccess: (id: string) => request<BoardAccessSettingsDto>(`/api/boards/${id}/access`),
  setBoardVisibility: (id: string, visibility: BoardVisibility) =>
    request<void>(`/api/boards/${id}/access`, {
      method: 'PUT',
      body: JSON.stringify({ visibility }),
    }),
  addBoardMember: (id: string, userId: string) =>
    request<void>(`/api/boards/${id}/access/members`, json({ userId })),
  removeBoardMember: (id: string, userId: string) =>
    request<void>(`/api/boards/${id}/access/members/${userId}`, { method: 'DELETE' }),

  templates: (orgId: string) =>
    request<TemplateDto[]>(`/api/templates?org=${encodeURIComponent(orgId)}`),
  createTemplate: (body: {
    boardId: string
    name: string
    scope: TemplateScope
    content: TemplateContent
  }) => request<TemplateDto>('/api/templates', json(body)),
  updateTemplate: (id: string, body: { name?: string; scope?: TemplateScope }) =>
    request<void>(`/api/templates/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteTemplate: (id: string) => request<void>(`/api/templates/${id}`, { method: 'DELETE' }),
  applyTemplate: (boardId: string, templateId: string) =>
    request<TemplateApplyDto>(`/api/boards/${boardId}/templates/${templateId}/apply`, {
      method: 'POST',
    }),
  boardTemplates: (orgId: string) =>
    request<BoardTemplateDto[]>(`/api/board-templates?org=${encodeURIComponent(orgId)}`),
  createBoardTemplate: (body: {
    orgId: string
    name: string
    scope: TemplateScope
    content: BoardTemplateContent
  }) => request<BoardTemplateDto>('/api/board-templates', json(body)),
  deleteBoardTemplate: (id: string) =>
    request<void>(`/api/board-templates/${id}`, { method: 'DELETE' }),

  board: (id: string) => request<BoardAccessDto>(`/api/boards/${id}`),
  duplicateBoard: (id: string) => request<BoardDto>(`/api/boards/${id}/duplicate`, { method: 'POST' }),
  renameBoard: (id: string, title: string) =>
    request<BoardDto>(`/api/boards/${id}`, { method: 'PATCH', body: JSON.stringify({ title }) }),
  deleteBoard: (id: string) => request<void>(`/api/boards/${id}`, { method: 'DELETE' }),

  shares: (boardId: string) => request<ShareLinkDto[]>(`/api/boards/${boardId}/shares`),
  createShare: (boardId: string, mode: ShareMode, greeting?: string) =>
    request<ShareLinkDto>(`/api/boards/${boardId}/shares`, json({ mode, greeting })),
  setShareGreeting: (token: string, greeting: string) =>
    request<void>(`/api/shares/${token}`, { method: 'PATCH', body: JSON.stringify({ greeting }) }),
  revokeShare: (token: string) => request<void>(`/api/shares/${token}`, { method: 'DELETE' }),
  /** Der Wartebildschirm, wie ein Gast ihn sähe — für die Vorschau im Freigabe-Dialog. */
  waitingRoom: (boardId: string) => request<WaitingRoomDto>(`/api/boards/${boardId}/waiting`),

  notes: (boardId: string) => request<FrameNotesDto>(`/api/boards/${boardId}/notes`),
  setNote: (boardId: string, frameId: string, text: string) =>
    request<void>(`/api/boards/${boardId}/notes/${frameId}`, {
      method: 'PUT',
      body: JSON.stringify({ text }),
    }),

  history: (boardId: string) => request<SnapshotDto[]>(`/api/boards/${boardId}/history`),
  saveSnapshot: (boardId: string, label?: string) =>
    request<SnapshotDto>(`/api/boards/${boardId}/history`, json({ label })),
  /** Der Rohzustand eines Standes; der Client baut daraus ein zweites Dokument. */
  snapshotState: async (boardId: string, snapshotId: string): Promise<Uint8Array> => {
    const response = await fetch(`/api/boards/${boardId}/history/${snapshotId}`, {
      credentials: 'same-origin',
    })
    if (!response.ok) {
      throw new ApiError(response.status, 'error', text()('history.loadFailed'))
    }
    return new Uint8Array(await response.arrayBuffer())
  },

  setOrgLogo: (orgId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<void>(`/api/orgs/${orgId}/logo`, { method: 'PUT', body: form })
  },
  removeOrgLogo: (orgId: string) => request<void>(`/api/orgs/${orgId}/logo`, { method: 'DELETE' }),
  /** Was der Beitrittsbildschirm zeigen muss, bevor der Gast eintritt. */
  guestLinkInfo: (token: string) =>
    request<{ boardId: string; boardTitle: string; mode: ShareMode; joinedAs: string | null }>(
      `/api/guest/${token}`,
    ),
  joinAsGuest: (token: string, name: string) =>
    request<{ boardId: string; mode: ShareMode; guestId: string; name: string; color: string }>(
      `/api/guest/${token}`,
      json({ name }),
    ),

  fonts: (orgId: string) => request<FontDto[]>(`/api/fonts?org=${encodeURIComponent(orgId)}`),
  addFont: (orgId: string, family: string) => request<FontDto>('/api/fonts', json({ orgId, family })),
  removeFont: (id: string) => request<void>(`/api/fonts/${id}`, { method: 'DELETE' }),

  media: (boardId: string) => request<MediaDto[]>(`/api/boards/${boardId}/media`),
  uploadPreview: (boardId: string, blob: Blob) => {
    const form = new FormData()
    form.append('file', blob, 'preview.webp')
    return request<void>(`/api/boards/${boardId}/preview`, { method: 'PUT', body: form })
  },
  /**
   * Eine Datei aufs Board legen. Die Antwort ist eine Liste, weil aus einem PDF
   * eine Seite pro Medium wird.
   */
  upload: (boardId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return request<UploadResultDto>(`/api/boards/${boardId}/media`, { method: 'POST', body: form })
  },

  /**
   * Eine eigene Schriftdatei hochladen. Die Felder stehen vor der Datei im
   * Formular: Der Server liest sie, sobald die Datei durch ist, und findet sie
   * nur dann schon vor.
   */
  uploadFont: (
    orgId: string,
    file: File,
    cut: { family: string; weight: number; style: 'normal' | 'italic' },
  ) => {
    const form = new FormData()
    form.append('orgId', orgId)
    form.append('family', cut.family)
    form.append('weight', String(cut.weight))
    form.append('style', cut.style)
    form.append('licensed', 'true')
    form.append('file', file)
    return request<FontDto>('/api/fonts/upload', { method: 'POST', body: form })
  },
}
